import {Stack} from '@commercial-helios/ui/src/components/stack'
import {tokens, colors} from '@commercial-helios/ui/theme'
import React, {Fragment, useEffect} from 'react'
import styled, {css} from 'styled-components'

import {scrollToBottom} from '@/utils/scroll'
import Image from '@helios/shared/components/image'
import logoAngled from '@helios/shared/images/icons/logo-angled.svg'
import logoHorizontal from '@helios/shared/images/icons/logo-horizontal.svg'
import menuCloseIcon from '@helios/shared/images/icons/menu-close.svg'
import {Text} from '@helios/shared/styles/typography'
import hasNonEmptyItems from '@helios/shared/utils/has-non-empty-items'
import mq from '@helios/shared/utils/media-query'

import type {State, Data} from './expense-policy-builder'

interface Props {
    state: State
    visible: boolean
    togglePreview: () => void
    data: Data['preview'][number]
    isPreviewMode?: boolean
}

const interpolateVar = (str: string, variable: string | number) => {
    const reg = new RegExp(/({{.*}})/, 'gi')
    const parts = str.split(reg)

    return parts.map((part, i) =>
        part.match(reg) ? <strong key={i}>{variable}</strong> : <Fragment key={i}>{part}</Fragment>
    )
}

const Preview = ({state, visible, togglePreview, data, isPreviewMode}: Props) => {
    const {welcome, accommodation, food, transport, conferences, remote, gifts, details} = state

    const ref = React.useRef(null)

    useEffect(() => {
        scrollToBottom(ref)
    }, [])

    return (
        <ScrollableContainer $visible={visible} ref={ref}>
            <OuterWrapper $isPreviewMode={isPreviewMode}>
                <Wrapper space={40} $isPreviewMode={isPreviewMode}>
                    {isPreviewMode ? (
                        <DesktopLogo src={logoAngled} alt="" style={{display: 'block'}} />
                    ) : (
                        <Header>
                            <DesktopLogo src={logoAngled} alt="" />
                            <MobileLogo src={logoHorizontal} alt="" />

                            <CloseBtn onClick={togglePreview}>
                                <img src={menuCloseIcon} alt="" />
                            </CloseBtn>
                        </Header>
                    )}

                    <div>
                        <Title as="h2" variant="title-base" id="company-name">
                            {interpolateVar(data.title, welcome.companyName)}
                        </Title>

                        <Paragraph>{data.subtitle}</Paragraph>
                    </div>

                    {hasNonEmptyItems([accommodation]) && (
                        <Section>
                            <Image image={data.accommodation_icon} />

                            <div>
                                <Heading>{data.accommodation_heading}</Heading>

                                {accommodation.howToBookTrips && (
                                    <Paragraph>
                                        {interpolateVar(
                                            data.how_to_book_trips,
                                            accommodation.howToBookTrips
                                        )}
                                    </Paragraph>
                                )}

                                {accommodation.budgetPerNight &&
                                    !accommodation.coveredByPerDiem && (
                                        <Paragraph>
                                            {interpolateVar(
                                                data.budget_per_night,
                                                accommodation.budgetPerNight
                                            )}
                                        </Paragraph>
                                    )}

                                {accommodation.coveredByPerDiem && (
                                    <Paragraph>{data.budget_per_night_per_diem}</Paragraph>
                                )}

                                {accommodation.extra && (
                                    <Paragraph>
                                        <strong>{accommodation.extra}</strong>
                                    </Paragraph>
                                )}
                            </div>
                        </Section>
                    )}

                    {hasNonEmptyItems([food]) && (
                        <Section>
                            <Image image={data.food_icon} />

                            <div>
                                <Heading>{data.food_heading}</Heading>

                                <Paragraph>{data.food_subtitle}</Paragraph>

                                <Paragraph>
                                    {(food.breakfastCoveredByPerDiem || food.breakfast) && (
                                        <>
                                            {data.breakfast} –{' '}
                                            {food.breakfastCoveredByPerDiem
                                                ? data.food_covered_by_per_diem
                                                : interpolateVar(data.food_budget, food.breakfast)}
                                            <br />
                                        </>
                                    )}
                                    {(food.lunchCoveredByPerDiem || food.lunch) && (
                                        <>
                                            {data.lunch} –{' '}
                                            {food.lunchCoveredByPerDiem
                                                ? data.food_covered_by_per_diem
                                                : interpolateVar(data.food_budget, food.lunch)}
                                            <br />
                                        </>
                                    )}
                                    {(food.dinnerCoveredByPerDiem || food.dinner) && (
                                        <>
                                            {data.dinner} –{' '}
                                            {food.dinnerCoveredByPerDiem
                                                ? data.food_covered_by_per_diem
                                                : interpolateVar(data.food_budget, food.dinner)}
                                        </>
                                    )}
                                </Paragraph>

                                {food.extra && (
                                    <Paragraph>
                                        <strong>{food.extra}</strong>
                                    </Paragraph>
                                )}
                            </div>
                        </Section>
                    )}

                    {hasNonEmptyItems([transport]) && (
                        <Section>
                            <Image image={data.transport_icon} />

                            <div>
                                <Heading>{data.transport_heading}</Heading>

                                <Paragraph>
                                    {transport.airTravelPolicy && (
                                        <>
                                            {data.air_travel_budget} –{' '}
                                            <strong>{transport.airTravelPolicy}</strong>
                                            <br />
                                        </>
                                    )}

                                    {transport.cityTravelPolicy && (
                                        <>
                                            {data.city_transport_budget} –{' '}
                                            <strong>{transport.cityTravelPolicy}</strong>
                                            <br />
                                        </>
                                    )}

                                    {transport.mileageAmount && (
                                        <>
                                            {data.mileage_budget} –{' '}
                                            <strong>{transport.mileageAmount}</strong>
                                            <br />
                                        </>
                                    )}
                                </Paragraph>

                                {transport.extra && (
                                    <Paragraph>
                                        <strong>{transport.extra}</strong>
                                    </Paragraph>
                                )}
                            </div>
                        </Section>
                    )}

                    {hasNonEmptyItems([conferences]) && (
                        <Section>
                            <Image image={data.conferences_icon} />

                            <div>
                                <Heading>{data.conferences_heading}</Heading>

                                {conferences.conferenceAmount && (
                                    <Paragraph>
                                        {interpolateVar(
                                            data.conference_amount,
                                            conferences.conferenceAmount
                                        )}
                                    </Paragraph>
                                )}

                                {conferences.extra && (
                                    <Paragraph>
                                        <strong>{conferences.extra}</strong>
                                    </Paragraph>
                                )}
                            </div>
                        </Section>
                    )}

                    {hasNonEmptyItems([remote]) && (
                        <Section>
                            <Image image={data.remote_icon} />

                            <div>
                                <Heading>{data.remote_heading}</Heading>

                                {remote.hardwareAmount && (
                                    <Paragraph>
                                        {interpolateVar(
                                            data.hardware_amount,
                                            remote.hardwareAmount
                                        )}
                                    </Paragraph>
                                )}

                                {remote.softwareAmount && (
                                    <Paragraph>
                                        {interpolateVar(
                                            data.software_amount,
                                            remote.softwareAmount
                                        )}
                                    </Paragraph>
                                )}

                                {remote.extra && (
                                    <Paragraph>
                                        <strong>{remote.extra}</strong>
                                    </Paragraph>
                                )}
                            </div>
                        </Section>
                    )}

                    {hasNonEmptyItems([gifts]) && (
                        <Section>
                            <Image image={data.gifts_icon} />

                            <div>
                                <Heading>{data.gifts_heading}</Heading>

                                {gifts.clientAmount && (
                                    <Paragraph>
                                        {interpolateVar(data.client_amount, gifts.clientAmount)}
                                    </Paragraph>
                                )}

                                {gifts.employeeAmount && (
                                    <Paragraph>
                                        {interpolateVar(data.employee_amount, gifts.employeeAmount)}
                                    </Paragraph>
                                )}

                                {gifts.extra && (
                                    <Paragraph>
                                        <strong>{gifts.extra}</strong>
                                    </Paragraph>
                                )}
                            </div>
                        </Section>
                    )}

                    {hasNonEmptyItems([details]) && (
                        <Section>
                            <div>
                                <Heading>{data.details_heading}</Heading>

                                {details.detailDays && (
                                    <Paragraph>
                                        {interpolateVar(data.detail_days, details.detailDays)}
                                    </Paragraph>
                                )}

                                {details.detailsReimbursement && (
                                    <Paragraph>
                                        {data.detail_reimbursements} -{' '}
                                        <strong>{details.detailsReimbursement}</strong>
                                    </Paragraph>
                                )}

                                {(details.contactName || details.contactEmail) && (
                                    <Paragraph>
                                        {interpolateVar(
                                            data.contact,
                                            `${details.contactName} ${
                                                details.contactEmail && details.contactName && ' ('
                                            }${details.contactEmail}${
                                                details.contactEmail && details.contactName && ')'
                                            } `
                                        )}
                                    </Paragraph>
                                )}

                                {details.extra && (
                                    <Paragraph>
                                        <strong>{details.extra}</strong>
                                    </Paragraph>
                                )}
                            </div>
                        </Section>
                    )}
                </Wrapper>
            </OuterWrapper>
        </ScrollableContainer>
    )
}

interface ScrollableContainerProps {
    $visible: boolean
}

const ScrollableContainer = styled.div<ScrollableContainerProps>`
    position: fixed;
    top: 0;
    left: 0;
    display: ${(props) => (props.$visible ? 'block' : 'none')};
    height: 100vh;
    overflow-y: auto;
    background: ${colors.background.muted};

    ${mq.min('md')} {
        position: relative;
        display: block;
    }

    ${mq.max('xs')} {
        height: -webkit-fill-available;
    }
`

interface OuterWrapperProps {
    $isPreviewMode?: boolean
}

const OuterWrapper = styled.div<OuterWrapperProps>(({$isPreviewMode}) => {
    const previewModeStyles = css`
        && {
            height: 100%;
            padding: 0;
        }
    `

    return css`
        height: 100%;

        ${mq.min('md')} {
            height: auto;
            padding: ${tokens.spacing40};
        }

        ${mq.min('lg')} {
            padding: 80px;
        }

        ${$isPreviewMode && previewModeStyles}
    `
})

interface WrapperProps {
    $isPreviewMode?: boolean
}

const Wrapper = styled(Stack)<WrapperProps>(({$isPreviewMode}) => {
    const previewModeStyles = css`
        height: 100%;
        background: none;
        border: none;
        box-shadow: none;

        strong {
            font-weight: inherit;
        }
    `

    return css`
        min-height: 100%;
        padding: ${tokens.spacing24};
        background: ${colors.background.default};
        border-bottom: 2px dashed ${colors.border.dark};
        box-shadow: ${tokens.shadowRaiseMedium};

        ${mq.min('md')} {
            padding: ${tokens.spacing60};
        }

        ${$isPreviewMode && previewModeStyles}
    `
})

const Header = styled.div`
    display: flex;
    flex-wrap: nowrap;
    justify-content: space-between;
    width: 100%;
`

const DesktopLogo = styled.img`
    display: none;
    height: 64px;

    ${mq.min('md')} {
        display: block;
    }
`

const MobileLogo = styled.img`
    display: block;
    height: 28px;

    ${mq.min('md')} {
        display: none;
    }
`

const CloseBtn = styled.button`
    padding: 0;
    cursor: pointer;
    background: none;
    border: none;

    img {
        display: block;
        width: auto;
        height: 24px;
    }

    ${mq.min('md')} {
        display: none;
    }
`

const Title = styled(Text)`
    :not(:last-child) {
        margin-bottom: ${tokens.spacing10};
    }
`

const Heading = styled(Text).attrs({as: 'h3'})`
    :not(:last-child) {
        margin-bottom: ${tokens.spacing4};
    }
`

const Paragraph = styled(Text).attrs({variant: 'paragraph-base', color: 'muted'})`
    :not(:last-child) {
        margin-bottom: ${tokens.spacing20};
    }
`

const Section = styled.div`
    display: grid;
    grid-template-columns: auto 1fr;
    column-gap: ${tokens.spacing12};

    img {
        width: 60px;
    }

    ${mq.min('lg')} {
        column-gap: ${tokens.spacing40};

        img {
            width: 80px;
        }
    }
`

export default Preview
